import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Loader2 } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [status, setStatus] = useState("pending"); // pending | paid | failed

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let attempts = 0;
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      attempts++;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setStatus("paid");
          return;
        }
        if (["failed", "expired", "refunded"].includes(data.payment_status)) {
          setStatus("failed");
          return;
        }
      } catch {}
      if (attempts >= 20) { setStatus("failed"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stopped = true; };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="max-w-md mx-auto w-full px-6 py-10 text-center">
        {status === "pending" && (
          <>
            <Loader2 className="mx-auto animate-spin text-slate-400" size={40} />
            <div className="mt-6 font-manrope text-2xl font-bold text-slate-900">Confirming your payment…</div>
            <p className="mt-2 text-slate-500 text-sm">This usually takes just a few seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-emerald-500" size={56} strokeWidth={1.75} />
            <div className="mt-6 font-manrope text-3xl font-extrabold tracking-tight text-slate-900">You're Premium.</div>
            <p className="mt-2 text-slate-500">Every insight, plan, and goal is now unlocked.</p>
            <Link to="/app" data-testid="success-continue-btn" className="inline-block mt-8 bg-slate-900 text-white rounded-full py-3 px-8 font-semibold hover:bg-slate-800 transition-colors">Continue</Link>
          </>
        )}
        {status === "failed" && (
          <>
            <div className="font-manrope text-2xl font-bold text-slate-900">We couldn't confirm your payment.</div>
            <p className="mt-2 text-slate-500 text-sm">If you were charged we'll sync automatically. You can also retry.</p>
            <Link to="/app/profile" className="inline-block mt-8 bg-slate-900 text-white rounded-full py-3 px-8 font-semibold hover:bg-slate-800 transition-colors">Back to profile</Link>
          </>
        )}
      </div>
    </div>
  );
}
